<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.TRANSACTIONS.DETAIL.SHOW"
    :handler="() => $refs.modal.show()"
  />

  <DialogComponent
    ref="modal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <div
      class="w-full  px-4 pb-6"
      :class="{ shimmerContainer: loading }"
    >
      <!-- operation type badge -->
      <div class="flex justify-center mb-6">
        <div
          class="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
          :class="{
            'bg-green-100 text-green-700': transaction?.operation == 'fund',
            'bg-red-100 text-red-700': transaction?.operation == 'withdraw'
          }"
        >
          <svg
            v-if="transaction?.operation == 'fund'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
          </svg>
          <svg
            v-if="transaction?.operation == 'withdraw'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
            <polyline points="17 18 23 18 23 12" />
          </svg>
          {{ transaction?.operationNameDisplay }}
        </div>
      </div>

      <!-- amount -->
      <div class="text-center mb-6">
        <div
          class="font-outfit font-bold text-4xl tracking-tight mb-1"
          :class="{
            'text-green-600': transaction?.operation == 'fund',
            'text-red-500': transaction?.operation == 'withdraw'
          }"
        >
          {{ formatAmount(transaction?.amountToReceive) }} <span class="text-2xl">FCFA</span>
        </div>
        <div class="text-sm text-gray-500">Montant reçu</div>
      </div>

      <!-- service logo -->
      <div class="flex justify-center mb-6">
        <div class="w-16 h-16 rounded-2xl bg-white shadow-lg p-3 flex items-center justify-center">
          <ImgComponent
            :src="transaction?.logo"
            alt=""
            class="w-full h-full object-contain"
          />
        </div>
      </div>

      <!-- details section -->
      <div class="space-y-3">
        <!-- client info -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 space-y-3">
            <div class="flex justify-between">
              <div class="text-sm text-gray-500">Client</div>
              <div class="capitalize font-medium text-gray-900">
                {{ $stores.user?.state?.current?.firstName }}
                {{ $stores.user?.state?.current?.lastName }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-sm text-gray-500">Email</div>
              <div class="font-medium text-gray-900">
                {{ $stores.user?.state?.current?.email }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-sm text-gray-500">Pays</div>
              <div class="capitalize font-medium text-gray-900">
                {{ Countries.find($stores.user?.state?.current?.countryCode)?.countryName }}
              </div>
            </div>
          </div>
        </div>

        <!-- status card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <ColorImg
                    src="/images/icons/info.svg"
                    class="w-5 h-5 bg-gray-600"
                  />
                </div>
                <div>
                  <div class="text-xs text-gray-500">Service</div>
                  <div class="font-semibold text-gray-900 capitalize">{{ transaction?.service }}</div>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <div class="text-xs text-gray-500">Date & heure</div>
                  <div class="font-semibold text-gray-900">{{ transaction?.startDate }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- amounts card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4">
            <div class="text-sm font-semibold text-gray-700 mb-3">Détails des montants</div>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Envoyé</span>
                <span class="font-semibold text-gray-900">{{ formatAmount(transaction?.amountToSend) }} FCFA</span>
              </div>
              <div v-if="getUsdtAmount('src')" class="flex justify-between items-center">
                <span class="text-xs text-gray-400 pl-3"></span>
                <span class="text-sm text-gray-400">({{ getUsdtAmount('src') }} USDT)</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Reçu</span>
                <span class="font-semibold text-gray-900">{{ formatAmount(transaction?.amountToReceive) }} FCFA</span>
              </div>
              <div v-if="getUsdtAmount('dst')" class="flex justify-between items-center">
                <span class="text-xs text-gray-400 pl-3"></span>
                <span class="text-sm text-gray-400">({{ getUsdtAmount('dst') }} USDT)</span>
              </div>

              <!-- fees -->
              <template v-if="hasFees">
                <div class="border-t border-gray-100 pt-3 mt-3">
                  <div class="space-y-2">
                    <div v-if="transaction?.feeBreakdown?.srcServiceFee" class="flex justify-between items-center text-sm">
                      <span class="text-gray-400">Frais service source</span>
                      <span class="text-gray-600">{{ formatAmount(transaction?.feeBreakdown?.srcServiceFee) }} FCFA</span>
                    </div>
                    <div v-if="transaction?.feeBreakdown?.dstServiceFee" class="flex justify-between items-center text-sm">
                      <span class="text-gray-400">Frais service dest.</span>
                      <span class="text-gray-600">{{ formatAmount(transaction?.feeBreakdown?.dstServiceFee) }} FCFA</span>
                    </div>
                    <div v-if="transaction?.feeBreakdown?.srcProcessorFee" class="flex justify-between items-center text-sm">
                      <span class="text-gray-400">Frais processeur source</span>
                      <span class="text-gray-600">{{ formatAmount(transaction?.feeBreakdown?.srcProcessorFee) }} FCFA</span>
                    </div>
                    <div v-if="transaction?.feeBreakdown?.dstProcessorFee" class="flex justify-between items-center text-sm">
                      <span class="text-gray-400">Frais processeur dest.</span>
                      <span class="text-gray-600">{{ formatAmount(transaction?.feeBreakdown?.dstProcessorFee) }} FCFA</span>
                    </div>
                    <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span class="font-medium text-gray-600">Total frais</span>
                      <span class="font-bold text-red-500">{{ formatAmount(transaction?.fee) }} FCFA</span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- bonus -->
              <div v-if="transaction?.bonus" class="flex justify-between items-center pt-2 border-t border-gray-100">
                <span class="font-medium text-gray-600">Bonus</span>
                <span class="font-bold text-green-600">+{{ formatAmount(transaction?.bonus) }} FCFA</span>
              </div>
            </div>
          </div>
        </div>

        <!-- statuses card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4">
            <div class="text-sm font-semibold text-gray-700 mb-3">Statuts</div>
            <div class="space-y-3">
              <div v-if="transaction?.operationDetails?.collectName" class="flex justify-between items-center">
                <span class="text-sm text-gray-500">{{ transaction?.operationDetails?.collectName }}</span>
                <span class="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                  {{ transaction?.collectStatusDisplay }}
                </span>
              </div>
              <div v-if="transaction?.operationDetails?.depositName" class="flex justify-between items-center">
                <span class="text-sm text-gray-500">{{ transaction?.operationDetails?.depositName }}</span>
                <span class="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                  {{ transaction?.depositStatusDisplay }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- additional info card -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4">
            <div class="text-sm font-semibold text-gray-700 mb-3">Informations additionnelles</div>
            <div class="space-y-3">
              <div v-if="transaction?.bookmakerId" class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Id {{ transaction?.service }}</span>
                <span class="font-medium text-gray-900">{{ transaction?.bookmakerId }}</span>
              </div>
              <div v-if="transaction?.rawData?.src?.phoneNumber || transaction?.senderPhoneNumber" class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Téléphone</span>
                <span class="font-medium text-gray-900">{{ transaction?.rawData?.src?.phoneNumber || transaction?.senderPhoneNumber }}</span>
              </div>
              <div v-if="transaction?.rawData?.dst?.phoneNumber || transaction?.recipientPhoneNumber" class="flex justify-between items-center">
                <span class="text-sm text-gray-500">Destinataire</span>
                <span class="font-medium text-gray-900">{{ transaction?.rawData?.dst?.phoneNumber || transaction?.recipientPhoneNumber }}</span>
              </div>
              <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                <span class="text-sm text-gray-500">Référence</span>
                <button
                  class="flex items-center gap-2 text-primary font-medium text-sm"
                  @click="copyReference"
                >
                  <span class="max-w-[150px] truncate">{{ transaction?.id }}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- message d'erreur -->
        <div
          v-if="(transaction?.message || transaction?.reason)"
          class="p-4 bg-red-50 rounded-2xl mt-4 border border-red-100"
        >
          <div class="text-red-500 font-semibold text-sm mb-1 uppercase tracking-wider">
            Note
          </div>
          <div class="text-red-900 font-bold leading-tight">
            {{ transaction?.message || transaction?.reason }}
          </div>
        </div>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
import Commons from '@/app/features/shared/libraries/commons';
import { Countries } from '@/app/features/shared/libraries/countries';

export default {
  name: 'TransactionDetailModal',

  setup() {
    return { Commons, Countries };
  },

  computed: {
    transaction() {
      return this.$stores.transactions?.state?.detail?.data;
    },
    loading() {
      return this.$stores.transactions?.state?.detail.loading;
    },
    hasFees() {
      const fb = this.transaction?.feeBreakdown;
      return (
        fb?.srcServiceFee ||
        fb?.dstServiceFee ||
        fb?.srcProcessorFee ||
        fb?.dstProcessorFee ||
        this.transaction?.fee > 0
      );
    }
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseInt(amount).toLocaleString('fr-FR');
    },

    getUsdtAmount(side) {
      const rawData = this.transaction?.rawData;
      if (!rawData) return null;

      const usdtData = rawData[side]?.data;
      if (usdtData?.usdtAmount) {
        return usdtData.usdtAmount;
      }
      return null;
    },

    copyReference() {
      Commons.copyToClipboard(
        this.transaction?.id,
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence copiée',
            body: this.transaction?.id
          });
        },
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence non copiée',
            body: 'veuillez réessayer'
          });
        }
      );
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

.font-outfit {
  font-family: 'Outfit', sans-serif;
}
</style>
